<template>
  <div id="customer-payment">
    <div class="col-heading">
      <h3 class>
        Enter payment details
      </h3>
    </div>
    <div v-if="!paymentSessionAvailable">
      <small class="text--error">Payment session unavailable, please refresh</small>
    </div>
    <div class="card col-md-6">
      <form
        id="card"
        class="mb-0"
      >
        <div class="mb-2 field field--medium-width">
          <label for="card-number">Card number</label>
          <input
            id="card-number"
            class="form-control"
            type="tel"
            readonly="readonly"
            maxlength="20"
            aria-describedby="Card Number"
          >
        </div>
        <div class="field">
          <div class="field__group mb-4">
            <span class="field__label">
              Expiry date
              <span class="field__label--hint">For example, 07/20</span>
            </span>
            <div class="field field--small-width">
              <label for="expiry-month">Month</label>
              <input
                id="expiry-month"
                class="form-control"
                type="tel"
                maxlength="2"
                aria-describedby="Month"
              >
            </div>

            <div class="field field--small-width">
              <label for="expiry-year">Year</label>
              <input
                id="expiry-year"
                class="form-control"
                type="tel"
                maxlength="2"
                aria-describedby="Year"
              >
            </div>
          </div>
        </div>

        <div class="field mb-2">
          <label
            for="security-code"
            class="field__label"
          >Security code</label>
          <div class="input input--small-width">
            <input
              id="security-code"
              class="form-control"
              type="tel"
              maxlength="4"
              readonly="readonly"
              aria-describedby="Security code"
            >
          </div>
        </div>

        <div class="field">
          <div class="input input--checkbox group">
            <span class="proxy-label field__label">Terms and conditions</span>
            <input
              id="AcceptedTermsAndConditions"
              v-model="termsAndConditionsAccepted"
              type="checkbox"
              class="input__checkbox"
            >
            <label
              for="AcceptedTermsAndConditions"
              class="input__checkbox-input"
            >Customer has accepted the Terms &amp; Conditions</label>
          </div>
        </div>

        <button
          :disabled="paymentButtonDisabled"
          type="button"
          class="btn btn-lg btn-primary mt-4"
          @click="updatePaymentSession"
        >
          <div v-if="paymentSessionUpdating">
            <i class="fa fa-spinner fa-spin" />
          </div>Make payment
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { mastercardConfigure } from '../../../assets/libs/mastercard'

export default {
    name: 'PaymentDetails',
    props: ['disabled'],
    data () {
        return {
            termsAndConditionsAccepted: false,
            paymentSessionUpdating: false,
            loadedSession: false
        }
    },
    computed: {
        bookingResponse: {
            get () {
                return this.$store.state.booking.bookingConfirmResponse
            },
            async set (value) {
                await this.$store.dispatch('setBookingCreationResponse', value)
            }
        },
        bookingAttempted () {
            return this.bookingResponse !== null
        },
        bookingSuccessful () {
            return this.bookingAttempted && this.bookingResponse.success
        },
        paymentButtonDisabled () {
            return (
                !this.termsAndConditionsAccepted ||
                (this.paymentSessionUpdating && !this.bookingAttempted) ||
                (this.bookingAttempted && this.bookingSuccessful) ||
                this.disabled
            )
        },
        paymentSessionAvailable () {
            return this.loadedSession
        },
        airportConfig () {
            return this.$store.getters['getSpecificConfiguration']({
                section: 'PaymentConfigurations',
                identifier: this.$store.getters['getSelectedAirport'].code
            })
        }
    },
    created () {
        if (document.getElementById('sessionJs') === null) {
            this.initialisePaymentSession()
        } else {
            this.loadedSession = true
        }
    },
    mounted () {
        if (document.getElementById('sessionJs') === null) {
            this.initialisePaymentSession()
        } else {
            this.loadedSession = true
        }
    },
    destroyed () {
        const sessionJs = document.getElementById('sessionJs')
        if (sessionJs !== null) {
            sessionJs.remove()
            this.loadedSession = false
        }
    },
    methods: {
        async initialisePaymentSession () {
            let sessionJs = document.createElement('script')
            let linkToMerchantSpecificMastercardSession =
                this.airportConfig.baseAddress +
                '/form' +
                '/version/' +
                this.airportConfig.apiVersion +
                '/merchant/' +
                this.airportConfig.merchant +
                '/session.js'
            sessionJs.setAttribute('src', linkToMerchantSpecificMastercardSession)
            sessionJs.setAttribute('id', 'sessionJs')
            sessionJs.setAttribute('async', 'false')
            sessionJs.onload = () => {
                PaymentSession.configure(mastercardConfigure(this))
                this.loadedSession = true
            }
            sessionJs.onerror = () => {
                this.$toasted.error(
                    'The payment session failed to initialise, preventing payment for this booking.'
                )
            }
            document.body.appendChild(sessionJs)
        },
        async updatePaymentSession () {
            if (this.paymentSessionAvailable) {
                this.paymentSessionUpdating = true
                this.bookingResponse = null
                PaymentSession.updateSessionFromForm('card')
            } else {
                this.$toasted.error(
                    'The payment session is not available, preventing payment for this booking.'
                )
            }
        },
        async processAffirmativeSessionResponse (sessionUpdateResponse) {
            const cardSummary = sessionUpdateResponse.sourceOfFunds.provided.card
            const paymentSession = {
                sessionId: sessionUpdateResponse.session.id,
                card: {
                    number: cardSummary.number,
                    expiryMonth: cardSummary.expiry.month,
                    expiryYear: cardSummary.expiry.year,
                    brand: cardSummary.brand,
                    scheme: cardSummary.scheme
                },
                merchant: sessionUpdateResponse.merchant,
                currency: this.airportConfig.currency
            }
            await this.$store.dispatch('setPaymentSession', paymentSession)
            this.paymentSessionUpdating = false
        },
        async processNegativeSessionResponse (sessionUpdateResponse, errorMessage) {
            this.paymentSessionUpdating = false
            this.$toasted.error(errorMessage)
        }
    }
}
</script>
